
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Calendar, Clock, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { useScoutTaskAssignments } from '@/hooks/useScoutTaskAssignments';

interface AthleteTasksTabProps {
  athleteId: string;
  athleteName: string;
}

// Extended interface to include optional debug fields
interface AssignmentWithDetails {
  id: string;
  task_id: string;
  athlete_profile_id: string;
  assigned_by: string;
  assigned_at: string;
  due_date?: string;
  notes?: string;
  status: 'new' | 'in_progress' | 'completed';
  updates?: string;
  created_at: string;
  updated_at: string;
  scout_task: {
    id: string;
    title: string;
    description?: string;
    priority: 'low' | 'normal' | 'high';
  };
  athlete_profile: {
    id: string;
    name?: string;
    username?: string;
  };
}

const AthleteTasksTab: React.FC<AthleteTasksTabProps> = ({
  athleteId,
  athleteName
}) => {
  const { assignments, isLoading } = useScoutTaskAssignments();

  // 🔍 DEBUG LOGGING - Scout Tasks in Athlete Dashboard
  console.log('🎯 AthleteTasksTab DEBUG - Full Data:', {
    athleteId,
    athleteName,
    isLoading,
    allAssignments: assignments,
    assignmentsCount: assignments.length,
    assignmentsRaw: JSON.stringify(assignments, null, 2)
  });

  // Filter tasks for this specific athlete
  const athleteTasks = assignments.filter(assignment => {
    const isMatch = assignment.athlete_profile_id === athleteId;
    console.log('🔍 AthleteTasksTab - Filter Logic:', {
      assignmentId: assignment.id,
      assignmentAthleteId: assignment.athlete_profile_id,
      targetAthleteId: athleteId,
      isMatch,
      taskTitle: assignment.scout_task?.title,
      taskStatus: assignment.status,
      fullAssignment: assignment
    });
    return isMatch;
  });

  console.log('🎯 AthleteTasksTab - After Filtering:', {
    originalCount: assignments.length,
    filteredCount: athleteTasks.length,
    filteredTasks: athleteTasks,
    athleteId,
    filterCriteria: 'assignment.athlete_profile_id === athleteId'
  });

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'in_progress':
        return <AlertCircle className="h-4 w-4 text-blue-500" />;
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'declined':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <AlertCircle className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'declined':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (isLoading) {
    console.log('🎯 AthleteTasksTab - Still loading assignments');
    return (
      <div className="space-y-4">
        <div className="animate-pulse">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="mb-3">
              <CardContent className="p-4">
                <div className="space-y-2">
                  <div className="h-4 bg-muted rounded w-3/4"></div>
                  <div className="h-3 bg-muted rounded w-1/2"></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (athleteTasks.length === 0) {
    console.log('🎯 AthleteTasksTab - No tasks found for athlete:', {
      athleteId,
      athleteName,
      totalAssignments: assignments.length,
      reason: assignments.length === 0 ? 'No assignments exist at all' : 'No assignments match this athlete ID'
    });
    
    return (
      <div className="text-center py-8">
        <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
        <h3 className="text-lg font-semibold mb-2">No Tasks Assigned</h3>
        <p className="text-muted-foreground">
          You haven't assigned any tasks to {athleteName} yet.
        </p>
        {/* 🔍 DEBUG INFO IN UI */}
        <details className="mt-4 text-left">
          <summary className="cursor-pointer text-sm text-blue-600">Debug Info</summary>
          <div className="text-xs bg-gray-100 p-2 rounded mt-2">
            <p><strong>Athlete ID:</strong> {athleteId}</p>
            <p><strong>Total Assignments:</strong> {assignments.length}</p>
            <p><strong>Filtered Tasks:</strong> {athleteTasks.length}</p>
          </div>
        </details>
      </div>
    );
  }

  console.log('🎯 AthleteTasksTab - Rendering tasks:', athleteTasks.length);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">
          Tasks for {athleteName} ({athleteTasks.length})
        </h3>
      </div>

      <div className="space-y-3">
        {athleteTasks.map((assignment) => (
          <Card key={assignment.id} className="border-l-4 border-l-blue-500">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="space-y-1">
                  <CardTitle className="text-base">{assignment.scout_task.title}</CardTitle>
                  <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                    {assignment.due_date && (
                      <div className="flex items-center space-x-1">
                        <Calendar className="h-3 w-3" />
                        <span>Due {new Date(assignment.due_date).toLocaleDateString()}</span>
                      </div>
                    )}
                    <span className="text-xs">
                      Assigned {new Date(assignment.assigned_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
                <div className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs ${getStatusColor(assignment.status)}`}>
                  {getStatusIcon(assignment.status)}
                  <span className="capitalize">{assignment.status}</span>
                </div>
              </div>
            </CardHeader>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default AthleteTasksTab;
